import { useEffect } from 'react'
import { META_BY_ROUTE } from '@/content/pages/pages.generated'
import { DEFAULT_OG_IMAGE, SITE_ORIGIN, absolute, pageTitle } from '@/lib/site'

/**
 * Keeps the document head in step with the current route.
 *
 * The build writes correct tags into each route's static HTML, which covers a
 * cold load and any crawler. Client-side navigation replaces no markup in
 * `<head>`, so without this every page after the first keeps the previous
 * page's title — and shared links carry the wrong one.
 *
 * Values come from `META_BY_ROUTE` — the same module `build-seo` reads, so a
 * client-side navigation cannot show different metadata than the static file
 * for that route. They go through `pageTitle` for the site-name suffix, as
 * `build-seo` does; without it this dropped " - Singulate" from the title and
 * og:title on every client-side navigation.
 */
export function DocumentHead({ route }: { route: string }) {
  useEffect(() => {
    const meta = META_BY_ROUTE.get(route)
    if (!meta) return

    const full = pageTitle(meta.title)
    if (meta.title) document.title = full

    const set = (selector: string, create: () => HTMLMetaElement, value: string) => {
      if (!value) return
      let el = document.head.querySelector<HTMLMetaElement>(selector)
      if (!el) {
        el = create()
        document.head.appendChild(el)
      }
      el.setAttribute('content', value)
    }

    set(
      'meta[name="description"]',
      () => Object.assign(document.createElement('meta'), { name: 'description' }),
      meta.description,
    )
    set(
      'meta[property="og:title"]',
      () => {
        const m = document.createElement('meta')
        m.setAttribute('property', 'og:title')
        return m
      },
      full,
    )
    set(
      'meta[property="og:description"]',
      () => {
        const m = document.createElement('meta')
        m.setAttribute('property', 'og:description')
        return m
      },
      meta.description,
    )

    // Canonical has to track the route too, or every page claims to be the
    // one that was loaded first.
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    const url = `${SITE_ORIGIN}${route === '/' ? '/' : route}`
    link.href = url

    // og:url and the twitter pair drift the same way the title did.
    set(
      'meta[property="og:url"]',
      () => {
        const m = document.createElement('meta')
        m.setAttribute('property', 'og:url')
        return m
      },
      url,
    )
    set(
      'meta[name="twitter:title"]',
      () => Object.assign(document.createElement('meta'), { name: 'twitter:title' }),
      full,
    )
    set(
      'meta[name="twitter:description"]',
      () => Object.assign(document.createElement('meta'), { name: 'twitter:description' }),
      meta.description,
    )

    const img = absolute(meta.ogImage || DEFAULT_OG_IMAGE)
    if (img) {
      set(
        'meta[property="og:image"]',
        () => {
          const m = document.createElement('meta')
          m.setAttribute('property', 'og:image')
          return m
        },
        img,
      )
      set(
        'meta[name="twitter:image"]',
        () => Object.assign(document.createElement('meta'), { name: 'twitter:image' }),
        img,
      )
    }
  }, [route])

  return null
}
